import { z } from "zod/v4";

import {
  AgenticLogicBestSellersQueryDescriptions,
  AgenticLogicCatalogScopeDescriptions,
  AgenticLogicInStockQueryDescriptions,
  AgenticLogicNewArrivalsQueryDescriptions,
  AgenticLogicRecommendationOptionsDescriptions,
  AgenticLogicRelatedByAttributeQueryDescriptions,
} from "../../../descriptions/agentic-logic.describe";
import { assertDescriptions } from "../../../descriptions/types";
import {
  AGENTIC_LOGIC_PRODUCT_QUERY_MAX_LIMIT,
  AgenticLogicProductOrderBySchema,
} from "./product-query";

// ---------------------------------------------------------------------------
// The typed catalog reads that compile to Shopify ADMIN GraphQL (and, for
// windowed best sellers, ShopifyQL) on the trusted side. Every accessor here
// hands the port STRUCTURED args — never a document — so the capability and
// the read cache key (a canonical hash of these args) fall out of the shape.
//
// Shared vocabulary:
//   scope      collections and/or tags a read is restricted to
//   timeWindow the trailing window a time-based read covers
//   limit      how many products the read resolves (platform ceiling applies)
//   excludeOutOfStock
//              drop products with no variant available for sale (post-fetch,
//              so `inventoryPolicy: CONTINUE` products are kept)
// ---------------------------------------------------------------------------

export const AgenticLogicTimeWindowList = ["24h", "7d", "30d", "90d"] as const;
export const AgenticLogicTimeWindowSchema = z.enum(AgenticLogicTimeWindowList);

export type AgenticLogicTimeWindow =
  (typeof AgenticLogicTimeWindowList)[number];

/** Trailing window in whole days — the unit both Admin search and ShopifyQL take. */
export const AGENTIC_LOGIC_TIME_WINDOW_DAYS: Record<
  AgenticLogicTimeWindow,
  number
> = {
  "24h": 1,
  "7d": 7,
  "30d": 30,
  "90d": 90,
};

export const AgenticLogicCatalogScopeSchema = z
  .object({
    collectionIds: z
      .array(z.string().min(1))
      .min(1)
      .optional()
      .describe(AgenticLogicCatalogScopeDescriptions.collectionIds),
    tags: z
      .array(z.string().min(1))
      .min(1)
      .optional()
      .describe(AgenticLogicCatalogScopeDescriptions.tags),
  })
  .describe(AgenticLogicCatalogScopeDescriptions._schema);

export type AgenticLogicCatalogScope = z.infer<
  typeof AgenticLogicCatalogScopeSchema
>;

const limitSchema = z
  .number()
  .int()
  .positive()
  .max(AGENTIC_LOGIC_PRODUCT_QUERY_MAX_LIMIT);

export const AgenticLogicBestSellersMetricList = ["units", "revenue"] as const;

export const AgenticLogicBestSellersMetricSchema = z.enum(
  AgenticLogicBestSellersMetricList
);

export type AgenticLogicBestSellersMetric =
  (typeof AgenticLogicBestSellersMetricList)[number];

export const AgenticLogicBestSellersQuerySchema = z
  .object({
    timeWindow: AgenticLogicTimeWindowSchema.optional().describe(
      AgenticLogicBestSellersQueryDescriptions.timeWindow
    ),
    metric: AgenticLogicBestSellersMetricSchema.optional().describe(
      AgenticLogicBestSellersQueryDescriptions.metric
    ),
    scope: AgenticLogicCatalogScopeSchema.optional().describe(
      AgenticLogicBestSellersQueryDescriptions.scope
    ),
    limit: limitSchema.describe(AgenticLogicBestSellersQueryDescriptions.limit),
    excludeOutOfStock: z
      .boolean()
      .optional()
      .describe(AgenticLogicBestSellersQueryDescriptions.excludeOutOfStock),
  })
  .describe(AgenticLogicBestSellersQueryDescriptions._schema);

export type AgenticLogicBestSellersQuery = z.infer<
  typeof AgenticLogicBestSellersQuerySchema
>;

export const AgenticLogicInStockQuerySchema = z
  .object({
    minQuantity: z
      .number()
      .int()
      .positive()
      .optional()
      .describe(AgenticLogicInStockQueryDescriptions.minQuantity),
    scope: AgenticLogicCatalogScopeSchema.optional().describe(
      AgenticLogicInStockQueryDescriptions.scope
    ),
    orderBy: AgenticLogicProductOrderBySchema.optional().describe(
      AgenticLogicInStockQueryDescriptions.orderBy
    ),
    limit: limitSchema.describe(AgenticLogicInStockQueryDescriptions.limit),
  })
  .describe(AgenticLogicInStockQueryDescriptions._schema);

export type AgenticLogicInStockQuery = z.infer<
  typeof AgenticLogicInStockQuerySchema
>;

export const AgenticLogicNewArrivalsDateFieldList = [
  "created",
  "published",
] as const;

export const AgenticLogicNewArrivalsDateFieldSchema = z.enum(
  AgenticLogicNewArrivalsDateFieldList
);

export type AgenticLogicNewArrivalsDateField =
  (typeof AgenticLogicNewArrivalsDateFieldList)[number];

export const AgenticLogicNewArrivalsQuerySchema = z
  .object({
    timeWindow: AgenticLogicTimeWindowSchema.optional().describe(
      AgenticLogicNewArrivalsQueryDescriptions.timeWindow
    ),
    dateField: AgenticLogicNewArrivalsDateFieldSchema.optional().describe(
      AgenticLogicNewArrivalsQueryDescriptions.dateField
    ),
    scope: AgenticLogicCatalogScopeSchema.optional().describe(
      AgenticLogicNewArrivalsQueryDescriptions.scope
    ),
    limit: limitSchema.describe(AgenticLogicNewArrivalsQueryDescriptions.limit),
    excludeOutOfStock: z
      .boolean()
      .optional()
      .describe(AgenticLogicNewArrivalsQueryDescriptions.excludeOutOfStock),
  })
  .describe(AgenticLogicNewArrivalsQueryDescriptions._schema);

export type AgenticLogicNewArrivalsQuery = z.infer<
  typeof AgenticLogicNewArrivalsQuerySchema
>;

export const AgenticLogicRelatedByAttributeList = [
  "tag",
  "productType",
  "vendor",
  "collection",
] as const;

export const AgenticLogicRelatedByAttributeSchema = z.enum(
  AgenticLogicRelatedByAttributeList
);

export type AgenticLogicRelatedByAttribute =
  (typeof AgenticLogicRelatedByAttributeList)[number];

export const AgenticLogicRelatedByAttributeQuerySchema = z
  .object({
    productId: z
      .string()
      .min(1)
      .describe(AgenticLogicRelatedByAttributeQueryDescriptions.productId),
    by: AgenticLogicRelatedByAttributeSchema.describe(
      AgenticLogicRelatedByAttributeQueryDescriptions.by
    ),
    orderBy: AgenticLogicProductOrderBySchema.optional().describe(
      AgenticLogicRelatedByAttributeQueryDescriptions.orderBy
    ),
    limit: limitSchema.describe(
      AgenticLogicRelatedByAttributeQueryDescriptions.limit
    ),
    excludeOutOfStock: z
      .boolean()
      .optional()
      .describe(
        AgenticLogicRelatedByAttributeQueryDescriptions.excludeOutOfStock
      ),
  })
  .describe(AgenticLogicRelatedByAttributeQueryDescriptions._schema);

export type AgenticLogicRelatedByAttributeQuery = z.infer<
  typeof AgenticLogicRelatedByAttributeQuerySchema
>;

/**
 * Post-fetch options a rule may hand `getProductRecommendations`. Shopify's
 * recommendation read takes no filters, so every one of these is applied by
 * the SDK over the rows Shopify returned — they narrow, never widen.
 */
export const AgenticLogicRecommendationOptionsSchema = z
  .object({
    limit: limitSchema
      .optional()
      .describe(AgenticLogicRecommendationOptionsDescriptions.limit),
    scope: AgenticLogicCatalogScopeSchema.optional().describe(
      AgenticLogicRecommendationOptionsDescriptions.scope
    ),
    excludeOutOfStock: z
      .boolean()
      .optional()
      .describe(
        AgenticLogicRecommendationOptionsDescriptions.excludeOutOfStock
      ),
  })
  .describe(AgenticLogicRecommendationOptionsDescriptions._schema);

export type AgenticLogicRecommendationOptions = z.infer<
  typeof AgenticLogicRecommendationOptionsSchema
>;

assertDescriptions<
  typeof AgenticLogicCatalogScopeDescriptions,
  AgenticLogicCatalogScope
>();
assertDescriptions<
  typeof AgenticLogicBestSellersQueryDescriptions,
  AgenticLogicBestSellersQuery
>();
assertDescriptions<
  typeof AgenticLogicInStockQueryDescriptions,
  AgenticLogicInStockQuery
>();
assertDescriptions<
  typeof AgenticLogicNewArrivalsQueryDescriptions,
  AgenticLogicNewArrivalsQuery
>();
assertDescriptions<
  typeof AgenticLogicRelatedByAttributeQueryDescriptions,
  AgenticLogicRelatedByAttributeQuery
>();
assertDescriptions<
  typeof AgenticLogicRecommendationOptionsDescriptions,
  AgenticLogicRecommendationOptions
>();
